import React from 'react';
import { Dimensions, RefreshControlProps, View } from 'react-native';
import Divider from '../Divider';
import Stack from '../Stack';
import Button, { MAX_BUTTON_WIDTH } from '../Button';
import { colors } from '../../../utils/theme';
import { getIntl } from '../../../utils/intl';
import Menu from '../Menu';
import { NestedScrollViewPortalHostContainer } from '../NestedScrollViewPortal';
import { Action } from './types';
import { mainContentPadding } from '../MainContentContainer';
import MaxWidthRestrictedContent, { RestrictedWidthVariant } from '../MaxWidthRestrictedContent';
import { useIsInSideTabContentContainer } from '../TabView/hooks';
import { PRScrollView } from '../../shared/Scroll';

const intl = getIntl('components.v2BaseComponents.ActionButtonLayout', {
    more: 'More',
});

export type ActionButtonLayoutOwnProps = {
    actions?: Action[];
    children: React.ReactNode;
    scrollable?: boolean;
    header?: React.ReactNode;
    whiteBackground?: boolean;
    restrictedWidthVariant?: RestrictedWidthVariant;
    disableBackground?: boolean;
    refreshControl?: React.ReactElement<RefreshControlProps>;
};

export type ActionButtonLayoutProps = ActionButtonLayoutOwnProps;

const getActionsCharacterCount = (actions: Action[]) => {
    return actions.reduce((acc, action) => acc + action.title.length, 0);
};

const getActionCount = (shownActions: Action[], initialCharacterCount: number) => {
    if (!shownActions.length) return 0;
    const screenWidth = Dimensions.get('window').width;
    const maxActions = screenWidth > 1000 ? 3 : 2;
    const maxActionCharacterCount = maxActions * 13; // this is a rough estimate.  This prevents long text buttons from overflowing
    const displayedActions: Action[] = [];
    const getDisplayedActionsCharacterCount = () =>
        displayedActions.reduce((acc, action) => acc + action.title.length, initialCharacterCount);
    let index = 0;
    while (
        index < shownActions.length &&
        getDisplayedActionsCharacterCount() + shownActions[index].title.length < maxActionCharacterCount
    ) {
        displayedActions.push(shownActions[index]);
        index++;
    }

    return Math.max(Math.min(displayedActions.length, maxActions), 1);
};

export const ACTION_BUTTON_LAYOUT_ACTION_BAR_HEIGHT = 67;

const ActionButtonLayout: React.FC<ActionButtonLayoutProps> = ({
    actions,
    children,
    scrollable,
    header,
    whiteBackground,
    restrictedWidthVariant,
    disableBackground,
    refreshControl,
}) => {
    const isInSideTabContentContainer = useIsInSideTabContentContainer();
    const shownActions = (actions || []).filter((action) => action.show !== false);

    const hasPrimaryAction = shownActions.some((action) => action.isPrimary);
    if (hasPrimaryAction && shownActions.length > 1) {
        throw new Error('Primary action must be the only action');
    }

    const backgroundColor = disableBackground
        ? undefined
        : whiteBackground || isInSideTabContentContainer
        ? colors.White
        : colors.Background;

    const renderBottomActions = () => {
        if (shownActions.length === 0) {
            return null;
        }

        const forcedMoreActions = shownActions.filter((action) => action.displayInMoreMenu);
        const forcedPresentActions = shownActions.filter((action) => action.displayInMoreMenu === false);
        const remainingActions = shownActions.filter((action) => action.displayInMoreMenu === undefined);
        const directlyListedActions = [
            ...forcedPresentActions,
            ...remainingActions.splice(
                0,
                getActionCount(remainingActions, getActionsCharacterCount(forcedPresentActions)),
            ),
        ];
        const popoverActions = [...forcedMoreActions, ...remainingActions];

        // A "More" menu hiding a single action is worse than just showing the action —
        // but respect actions explicitly forced into the menu (displayInMoreMenu).
        if (popoverActions.length === 1 && forcedMoreActions.length === 0) {
            directlyListedActions.push(popoverActions.pop()!);
        }

        const renderE2EIndicator = (isInMenu: boolean, testID: string) => {
            const indicatorTestID = `action-button-indicator-${testID}-${isInMenu ? 'menu' : 'direct'}`;
            return (
                <View key={indicatorTestID} testID={indicatorTestID} collapsable={false} style={{ display: 'none' }} />
            );
        };

        return (
            <View
                style={{
                    height: ACTION_BUTTON_LAYOUT_ACTION_BAR_HEIGHT,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'visible',
                    backgroundColor: colors.White,
                }}
            >
                <Divider horizontal />
                <MaxWidthRestrictedContent
                    style={{ height: '100%' }}
                    contentContainerStyle={{
                        height: '100%',
                    }}
                >
                    {directlyListedActions
                        .filter((_) => _.testID)
                        .map((action) => renderE2EIndicator(false, action.testID!))}
                    {popoverActions.filter((_) => _.testID).map((action) => renderE2EIndicator(true, action.testID!))}
                    <PRScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        style={{ width: '100%', height: '100%' }}
                        contentContainerStyle={{ width: '100%', height: '100%' }}
                    >
                        <Stack
                            horizontal
                            justify="center"
                            align="center"
                            fullHeight
                            style={{
                                backgroundColor: colors.White,
                                flex: 1,
                                paddingLeft: mainContentPadding,
                                paddingRight: mainContentPadding,
                            }}
                            space={2.5}
                            testID="action-button-container"
                        >
                            {directlyListedActions.map((action, index) => (
                                <View key={index} style={{ flex: 1, maxWidth: MAX_BUTTON_WIDTH }}>
                                    <Button
                                        key={index}
                                        onPress={action.onPress}
                                        icon={action.icon}
                                        isDisabled={action.isDisabled}
                                        size={'lg'}
                                        title={action.title}
                                        variant={action.variant || 'primary'}
                                        testID={action.testID}
                                        onLayout={() => {}}
                                    />
                                </View>
                            ))}
                            {popoverActions.length > 0 && (
                                <Menu
                                    renderButton={({ ref, onPress, onLayout, isDisabled }) => (
                                        <Button
                                            ref={ref}
                                            onPress={onPress}
                                            icon="Menu"
                                            title={intl('more')}
                                            onLayout={onLayout}
                                            testID="action-button-more"
                                            isDisabled={isDisabled}
                                            style={{ width: 120 }}
                                        />
                                    )}
                                    popoverPosition="bottom-right-align"
                                    items={popoverActions.map((action) => ({
                                        title: action.title,
                                        onPress: action.onPress,
                                        isDisabled: action.isDisabled,
                                        icon: action.icon,
                                        testID: action.testID,
                                    }))}
                                />
                            )}
                        </Stack>
                    </PRScrollView>
                </MaxWidthRestrictedContent>
            </View>
        );
    };

    const maybeRenderInScrollable = (content: React.ReactNode) => {
        if (scrollable) {
            return (
                <PRScrollView
                    showsVerticalScrollIndicator={true}
                    testID="action-button-layout-scroll-view"
                    style={{ width: '100%', height: '100%', backgroundColor }}
                    contentContainerStyle={{ flexGrow: 1, width: '100%' }}
                    refreshControl={refreshControl}
                >
                    {content}
                </PRScrollView>
            );
        } else {
            return <View style={{ width: '100%', flex: 1, height: '100%', backgroundColor }}>{content}</View>;
        }
    };

    const renderHeader = () => {
        if (!header) {
            return null;
        }

        return (
            <>
                <View style={{ width: '100%', backgroundColor: colors.White }}>{}</View>
                <Divider horizontal />
            </>
        );
    };

    return (
        <NestedScrollViewPortalHostContainer>
            <View
                style={{ width: '100%', flex: 1, overflow: 'hidden' }}
                testID="action-button-layout"
                collapsable={false}
            >
                <MaxWidthRestrictedContent
                    style={{ flex: 1 }}
                    contentContainerStyle={{
                        backgroundColor: whiteBackground || isInSideTabContentContainer ? colors.White : undefined,
                        flex: 1,
                        height: '100%',
                    }}
                    variant={restrictedWidthVariant}
                >
                    {maybeRenderInScrollable(
                        <>
                            {renderHeader()}
                            {children}
                        </>,
                    )}
                </MaxWidthRestrictedContent>
                {renderBottomActions()}
            </View>
        </NestedScrollViewPortalHostContainer>
    );
};

export default React.memo(ActionButtonLayout);
